import React from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Draw as DrawIcon,
  BarChart as BarChartIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

const steps = [
  {
    icon: <DescriptionIcon sx={{ fontSize: 36 }} />,
    color: '#7c3aed',
    bg: '#f3e8ff',
    title: 'Review Your Estimate',
    description:
      'Your contractor builds a detailed estimate with every room and line item — materials, labor, and costs. You see the full breakdown before anything starts.',
  },
  {
    icon: <DrawIcon sx={{ fontSize: 36 }} />,
    color: '#16a34a',
    bg: '#dcfce7',
    title: 'Sign to Approve',
    description:
      "Once you've reviewed the estimate, sign digitally to approve it. If something looks off, reject it with feedback so your contractor can revise.",
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 36 }} />,
    color: '#ea580c',
    bg: '#ffedd5',
    title: 'Track Progress',
    description:
      "As work proceeds, monitor your project's progress and costs directly in the app. Nothing starts without your approval, and you're always in the loop.",
  },
];

const HomeownerWelcome = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const token = searchParams.get('token');
  const invitedBy = searchParams.get('invited_by');

  // If the visitor is already logged in (e.g. they set up their account and
  // came back to the invite link), don't show the "log in to accept" screen —
  // their invite is already accepted, so send them straight to their dashboard.
  if (loading) return null;
  if (user) {
    const dest = user.role === 'contractor' ? '/contractor'
      : user.role === 'admin' ? '/admin'
      : '/homeowner';
    return <Navigate to={dest} replace />;
  }

  const handleSetup = () => {
    if (token) {
      navigate(`/reset-password?token=${token}`);
    } else {
      navigate('/register?role=homeowner');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0d47a1 0%, #1976d2 40%, #f5f7fa 40%)',
        display: 'flex',
        alignItems: 'center',
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: '#0d47a1',
              px: 4,
              py: 4,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <HomeIcon sx={{ color: 'white', fontSize: 34 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} color="white" gutterBottom>
              Welcome to Congrow
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {invitedBy
                ? `${invitedBy} has invited you to track and approve your home improvement project.`
                : 'Your contractor has invited you to track and approve your home improvement project.'}
            </Typography>
          </Box>

          {/* Steps */}
          <Box sx={{ px: 4, pt: 4, pb: 3 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={2}
            >
              How it works
            </Typography>

            <Stack spacing={3}>
              {steps.map((step, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      bgcolor: step.bg,
                      color: step.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ mx: 4 }} />

          {/* CTA */}
          <Box sx={{ px: 4, py: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSetup}
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 600 }}
            >
              {token ? 'Set Up My Account' : 'Create My Account'}
            </Button>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mt={2}
            >
              Already have an account?{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login?role=homeowner')}
                sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline' }}
              >
                Log in
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default HomeownerWelcome;
